import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isDesktopApp } from '@/api/desktopSync';
import useRendererOperation from './useRendererOperation';
const STATES = new Set(['idle', 'downloading', 'downloaded', 'retry-ready', 'blocked', 'interrupted']);
export function validUpdateStatus(value) {
  return value && typeof value === 'object' && !Array.isArray(value) && STATES.has(value.state)
    && (value.version === undefined || typeof value.version === 'string')
    && (value.percent === undefined || Number.isFinite(value.percent));
}
// Device capability: deliberately independent of authenticated/admin session ownership.
export default function useDesktopUpdateOwner() {
  const bridge = typeof window === 'undefined' ? null : window.lvbDesktop;
  const latest = useRef(bridge); latest.current = bridge;
  const owner = useMemo(() => ({ bridge, active: false, sequence: 0, value: { state: 'idle' }, phase: 'loading', pending: new Set() }), [bridge]);
  const [view, setView] = useState(null);
  const operation = useRendererOperation(bridge, 'application-update');
  const current = useCallback(() => owner.active && isDesktopApp() && latest.current === bridge && window.lvbDesktop === bridge, [owner, bridge]);
  const publish = useCallback(() => {
    if (current()) setView({ owner, status: owner.value, phase: owner.phase });
  }, [current, owner]);
  const refresh = useCallback(async () => {
    if (!current() || typeof bridge?.getUpdateStatus !== 'function') return;
    const sequence = ++owner.sequence;
    let timer; let retire;
    const expiry = new Promise((_, reject) => { retire = () => reject(new Error('Status unavailable')); timer = setTimeout(retire, 30000); });
    owner.pending.add(retire);
    try {
      const value = await Promise.race([Promise.resolve().then(() => {
        if (!current()) throw new Error('Retired');
        return bridge.getUpdateStatus();
      }), expiry]);
      if (!validUpdateStatus(value)) throw new Error('Malformed status');
      if (current() && owner.sequence === sequence) { owner.value = value; owner.phase = 'ready'; publish(); }
    } catch {
      if (current() && owner.sequence === sequence) { owner.phase = 'unavailable'; publish(); }
    } finally { clearTimeout(timer); owner.pending.delete(retire); }
  }, [current, bridge, owner, publish]);
  useEffect(() => {
    owner.active = true;
    let unsubscribe;
    if (current()) {
      try {
        unsubscribe = bridge?.onUpdateStatus?.(value => {
          if (!current()) return;
          owner.sequence++;
          if (validUpdateStatus(value)) { owner.value = value; owner.phase = 'ready'; }
          else owner.phase = 'unavailable';
          publish();
        });
      } catch { /* Optional status subscription; snapshot can still work. */ }
      refresh();
    }
    return () => { owner.active = false; for (const retire of owner.pending) retire(); unsubscribe?.(); };
  }, [owner, bridge, current, refresh, publish]);
  const beginInstall = async () => {
    if (!current() || owner.phase !== 'ready' || !['downloaded', 'retry-ready'].includes(owner.value.state)
      || typeof bridge?.installUpdate !== 'function') return;
    const token = operation.acquire('install');
    if (!token) return;
    let phase = 'unknown';
    try {
      const result = await bridge.installUpdate();
      if (result?.pending === true) phase = 'unknown';
      else if (result?.ok === true) phase = 'handoff';
      else if (result?.ok === false && (result.cancelled === true || typeof result.message === 'string')) phase = null;
      // Refusal/cancellation refreshes the observation, but never dispatches installation again.
      if (phase === null && current()) await refresh();
    } catch { /* Unconfirmed effects retain exclusion, including after remount. */ }
    finally { operation.settle(token, phase); }
  };
  const visible = view?.owner === owner ? view : null;
  return { status: visible?.status || { state: 'idle' }, phase: visible?.phase || 'loading',
    claim: operation.claim, beginInstall, refresh };
}
